import React, { useEffect, useState, useRef, useMemo } from "react";
import { PencilIcon } from "icons/outline";
import EditPopOverForm from "../EditPopOverForm";
import { useRouter } from "next/router";
import { IconButton, cn, OverflowText, Sheet } from "ui";
import { TableLoader } from "./TableLoader";
import { SidePanelPreviewItem } from "./SidePanelPreviewItem";
import { Table as TanstackTable, flexRender } from "@tanstack/react-table";
import SortIcon from "../SortIcon";

type TableProps = {
  previews: any[];
  customPreview: any;
  applySort: (column: string) => void;
  tableSorts: Record<string, "asc" | "desc">;
  editEnabled: boolean;
  formURL: string;
  tableName: string;
  refreshTable: () => void;
  onRowClick?: (
    rowIndex: number,
    cell: { row: { id: string } },
    preview: any
  ) => void;
  onEditItemClick?: (id: string, row?: any, preview?: any) => void;
  tableInstance: TanstackTable<object>;
  bulkActionErrorIds: string[];
  showSuccessMessage: (message: { name: string; action: string }) => void;
  isLoading: boolean;
  enableRowClickNavigation?: boolean | { newTab: boolean };
  getPreviewPath?: (row: any) => string;
  previewMode?: "side-panel" | "expanded";
  getRowId?: (row: any) => string;
  subComponentRender?: (row: any) => React.ReactNode;
};

const Table = React.memo(
  ({
    previews,
    customPreview,
    applySort,
    tableSorts,
    editEnabled,
    formURL,
    tableName,
    refreshTable,
    onRowClick,
    onEditItemClick,
    tableInstance,
    bulkActionErrorIds,
    showSuccessMessage,
    isLoading,
    enableRowClickNavigation,
    getPreviewPath,
    previewMode,
    getRowId,
    subComponentRender,
  }: TableProps) => {
    const router = useRouter();
    // if Content is an object, we will not render it as react component
    const hasFullCustomPreview = typeof customPreview?.Content !== "object";
    const [rowIDIntoPanel, setRowIDIntoPanel] = useState(null);
    const [panelDetails, setPanelDetails] = useState([]);
    const [editPopOverFormOpen, setEditPopOverFormOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const panelDetailsList = useMemo(
      () =>
        Object.entries(panelDetails).map(([key, value]) => ({ key, value })),
      [panelDetails]
    );

    useEffect(() => {
      // reset to not keep visible while paginating
      setRowIDIntoPanel(null);
    }, [isLoading]);

    function onRowClickAction(rowIndex: number, cell: any, preview: any) {
      if (["selection", "option"].indexOf(cell.column.id) !== -1) return; // don't show preview for selection and option column

      if (onRowClick) {
        onRowClick(rowIndex, cell, preview);
      } else {
        setDetailsIntoPanel(rowIndex);
      }
    }

    useEffect(() => {
      if (router.query.preview && previews && !isLoading) {
        const previewIndex = previews.findIndex?.(
          (p) => String(p.id) === router.query.preview
        );
        if (previewIndex >= 0) setDetailsIntoPanel(previewIndex);
        const { preview: _preview, ...queryWithoutPreview } = router.query;
        router.replace({ query: queryWithoutPreview });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.query.preview, previews]);

    // Fixed table column scroll detector
    useEffect(() => {
      if (!containerRef.current) return;

      const handleScroll = (e: any) => {
        const scrollAtEnd =
          e.target.scrollLeft + e.target.offsetWidth >= e.target.scrollWidth;
        e.target.dataset["scrollAtEnd"] = scrollAtEnd;
      };

      handleScroll({ target: containerRef.current });
      const container = containerRef.current;
      container.addEventListener("scroll", handleScroll);

      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }, []);

    function setDetailsIntoPanel(rowIndex: number) {
      const details = previews[rowIndex];

      // toggle panel if previewMode is expanded
      if (previewMode === "expanded") {
        setPanelDetails((prev) => (prev === details ? [] : details));
        setRowIDIntoPanel((prev) =>
          prev === details["id"] ? null : details["id"]
        );
      } else {
        setPanelDetails(details);
        setRowIDIntoPanel(details["id"]);
      }
    }

    return (
      <div className="flex flex-row sm:rounded-lg">
        <div style={{ width: "100%" }}>
          <div
            ref={containerRef}
            className="group relative min-w-full overflow-x-auto border border-foundations-subtle sm:rounded-lg"
          >
            <TableLoader isLoading={isLoading} />
            <table className="min-w-full">
              <thead className="border-b border-inherit">
                {tableInstance.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={cn(
                          `whitespace-nowrap px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-foundations-boldest`,
                          header.column.columnDef.meta?.className,
                          header.column.getCanSort() &&
                            "cursor-pointer select-none"
                        )}
                        colSpan={header.colSpan}
                        onClick={() => {
                          if (header.column.getCanSort()) {
                            applySort(header.column.id);
                          }
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div className="flex items-center">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}

                            {header.column.getCanSort() && (
                              <SortIcon
                                isSorting={!!tableSorts[header.column.id]}
                                isSortedAsc={
                                  tableSorts[header.column.id] === "asc"
                                }
                              />
                            )}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {tableInstance.getRowModel().rows.map((row, rowIndex) => {
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={cn(
                          "relative cursor-pointer",
                          rowIDIntoPanel === row.id
                            ? "border-b-0 text-foundations-bolder after:absolute after:left-0 after:top-0 after:h-full after:w-[2px] after:bg-foundations-bolder"
                            : "text-foundations-subtle",
                          rowIDIntoPanel === row.id &&
                            previewMode === "side-panel" &&
                            "shadow"
                        )}
                      >
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              onClick={(e) => {
                                const targetEl = e.target as HTMLElement;
                                const isLink = targetEl.closest("a");
                                if (isLink) return;

                                if (
                                  enableRowClickNavigation &&
                                  getPreviewPath
                                ) {
                                  const href = getPreviewPath(cell.row);
                                  if (e.metaKey) {
                                    window.open(href, "_blank");
                                  } else {
                                    typeof enableRowClickNavigation ===
                                      "object" &&
                                    enableRowClickNavigation.newTab
                                      ? window.open(href, "_blank")
                                      : router.push(href);
                                  }

                                  return;
                                }
                                onRowClickAction(
                                  rowIndex,
                                  cell,
                                  previews[rowIndex]
                                ); // idex>0 to prevent panel details if user click on select
                              }}
                              className={cn(
                                "whitespace-nowrap border-b border-foundations-subtle px-6 py-4 text-sm font-normal",
                                cell.column.columnDef.meta?.className,
                                bulkActionErrorIds.includes(row.id) &&
                                  "!border-danger-emphasis !text-danger"
                              )}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {row.getIsExpanded() && !!subComponentRender && (
                        <tr>
                          <td colSpan={row.getVisibleCells().length}>
                            {subComponentRender(row.original)}
                          </td>
                        </tr>
                      )}

                      {previewMode === "expanded" &&
                      rowIDIntoPanel === row.id ? (
                        <tr className="border-b border-b-foundations-subtle">
                          <td colSpan={row.getVisibleCells().length}>
                            {customPreview.Content && (
                              <customPreview.Content preview={panelDetails} />
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <Sheet
          open={rowIDIntoPanel !== null}
          onOpenChange={() => setRowIDIntoPanel(null)}
          noBackdrop
        >
          <Sheet.Popup>
            {previewMode === "side-panel" && rowIDIntoPanel !== null ? (
              <>
                <Sheet.Header>
                  <Sheet.SubHeading>{tableName}</Sheet.SubHeading>
                  <OverflowText render={<Sheet.Title />}>
                    {customPreview?.title ?? panelDetailsList[0]?.value}
                  </OverflowText>
                </Sheet.Header>

                <Sheet.Panel>
                  {customPreview?.Content && hasFullCustomPreview ? (
                    <customPreview.Content
                      key={rowIDIntoPanel}
                      preview={panelDetails}
                    />
                  ) : (
                    <div className="space-y-5">
                      {panelDetailsList.slice(1).map((item) => (
                        <div key={item.key}>
                          {/* NOTE: don't change itemKey to key as this is a reserved react prop */}
                          {customPreview?.Content?.[item.key]?.(
                            panelDetails
                          ) ?? (
                            <SidePanelPreviewItem
                              itemKey={item.key}
                              itemValue={item.value}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Sheet.Panel>

                {editEnabled && !customPreview?.Actions ? (
                  <Sheet.Footer>
                    {onEditItemClick ? (
                      <span className="mr-1">
                        <IconButton
                          variant="secondary"
                          className="px-4"
                          onClick={() => {
                            const selectedRow = previews.find((p) => {
                              const id = getRowId ? getRowId(p) : p.id;
                              return id === panelDetailsList[0]?.value;
                            });

                            onEditItemClick(
                              panelDetailsList[0]?.value,
                              selectedRow,
                              selectedRow
                            );
                          }}
                        >
                          <PencilIcon className="" aria-hidden="true" />
                          <span>Edit</span>
                        </IconButton>
                      </span>
                    ) : (
                      <EditPopOverForm
                        // @ts-ignore
                        Trigger={({ onClick }) => (
                          <IconButton
                            variant="secondary"
                            className="px-4"
                            onClick={onClick}
                          >
                            <PencilIcon aria-hidden="true" />
                            <span>Edit</span>
                          </IconButton>
                        )}
                        open={editPopOverFormOpen}
                        setOpen={setEditPopOverFormOpen}
                        url={`${formURL}${panelDetailsList[0]?.value}/`}
                        title={`Edit ${tableName}`}
                        // @ts-ignore
                        onSuccess={(name) => {
                          setRowIDIntoPanel(null);
                          showSuccessMessage({ name, action: "edited" });
                          refreshTable();
                        }}
                      />
                    )}
                  </Sheet.Footer>
                ) : customPreview?.Actions ? (
                  <customPreview.Actions
                    key={rowIDIntoPanel}
                    preview={panelDetails}
                  />
                ) : null}
              </>
            ) : null}
          </Sheet.Popup>
        </Sheet>
      </div>
    );
  }
);
Table.displayName = "Table";
export default Table;
